import { FEEDBACK_MAX_ATTACHMENTS, type FeedbackKind } from '@loot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { Bug, ImagePlus, Lightbulb, X } from 'lucide-react';
import { useEffect, useRef, useState, type ClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from './api.ts';
import { Button, ErrorBox, Field, Input, Textarea } from './components/ui.tsx';

const IMAGE_RE = /^image\/(png|jpeg|gif|webp)$/;

export const FEEDBACK_SETTINGS_KEY = ['feedback', 'settings'] as const;

/** Se o botão está ligado (Sistema › Bugs e melhorias). Na dúvida, não aparece. */
export function useFeedbackSettings() {
  return useQuery({
    queryKey: FEEDBACK_SETTINGS_KEY,
    queryFn: () => api.get<{ buttonEnabled: boolean }>('/feedback/settings'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Botão flutuante "Reportar", no canto inferior direito de todas as telas: abre o formulário de
 * bug ou melhoria. Vermelho e com o texto inteiro, para ser fácil de achar. Some quando quem
 * trata os reportes o desliga.
 */
export function ReportButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const settings = useFeedbackSettings();
  // Só aparece quando a configuração confirma que está ligado (sem piscar enquanto carrega).
  if (settings.data?.buttonEnabled !== true) return null;
  const label = t('feedback.button');
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-4 bottom-4 z-40 flex min-h-11 items-center gap-2 rounded-full bg-danger px-4 text-sm font-semibold text-sheet shadow-[0_6px_18px_rgb(23_32_43/0.28)] ring-2 ring-sheet hover:brightness-110 md:right-6 md:bottom-6 md:min-h-12 md:px-5 print:hidden"
      >
        <Bug size={18} strokeWidth={2} aria-hidden />
        {label}
      </button>
      {open && <ReportDialog onClose={() => setOpen(false)} />}
    </>
  );
}

interface Shot {
  file: File;
  url: string;
}

/**
 * Formulário do reporte. A tela em que a pessoa está, o navegador, o tamanho da tela e o idioma
 * vão juntos sozinhos; quem reportou e quando o servidor registra.
 */
function ReportDialog({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const location = useRouterState({ select: (s) => s.location });
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [shots, setShots] = useState<Shot[]>([]);
  const [tooMany, setTooMany] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // A tela é a de quando o formulário abriu (o endereço não muda enquanto ele está aberto).
  const [pagePath] = useState(() => `${location.pathname}${location.searchStr ?? ''}`);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Libera as prévias das imagens quando o formulário fecha.
  const shotsRef = useRef(shots);
  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);
  useEffect(() => () => shotsRef.current.forEach((s) => URL.revokeObjectURL(s.url)), []);

  const addFiles = (files: File[]) => {
    const images = files.filter((f) => IMAGE_RE.test(f.type));
    const room = FEEDBACK_MAX_ATTACHMENTS - shots.length;
    setTooMany(images.length > room);
    if (room <= 0 || images.length === 0) return;
    setShots((s) => [
      ...s,
      ...images.slice(0, room).map((file, i) => {
        // Imagem colada vem sem nome útil ("image.png"): ganha um nome com a hora.
        const name =
          file.name && file.name !== 'image.png'
            ? file.name
            : `print-${Date.now()}-${i + 1}.${file.type.split('/')[1]!.replace('jpeg', 'jpg')}`;
        const named = new File([file], name, { type: file.type });
        return { file: named, url: URL.createObjectURL(named) };
      }),
    ]);
  };
  const onPaste = (e: ClipboardEvent) => {
    const files = [...e.clipboardData.files];
    if (files.some((f) => IMAGE_RE.test(f.type))) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const send = useMutation({
    mutationFn: async () => {
      const created = await api.post<{ id: string; number: number }>('/feedback/reports', {
        kind,
        title,
        description,
        pagePath,
        pageTitle: document.title || null,
        userAgent: navigator.userAgent.slice(0, 500),
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        locale: i18n.language,
      });
      // O reporte já foi gravado: um print recusado não o desfaz, só fica avisado.
      let failed = 0;
      for (const s of shots) {
        const form = new FormData();
        form.append('file', s.file);
        try {
          await api.upload(`/feedback/reports/${created.id}/attachments`, form);
        } catch {
          failed += 1;
        }
      }
      return { ...created, failed };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feedback'] }),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/20 px-4 py-[8vh] backdrop-blur-[2px]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        onPaste={onPaste}
        className="flex w-full max-w-lg flex-col gap-4 rounded-lg border border-rule bg-sheet p-5 shadow-[0_12px_32px_rgb(23_32_43/0.16)]"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 id="feedback-title" className="text-lg font-semibold">
              {t('feedback.dialogTitle')}
            </h2>
            <p className="text-sm text-muted">{t('feedback.dialogIntro')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('nav.close')}
            className="rounded-md p-1 text-muted hover:text-ink"
          >
            <X size={18} aria-hidden />
          </button>
        </div>

        {send.isSuccess ? (
          <div className="flex flex-col gap-4">
            <p role="status" className="rounded-lg bg-ok/10 p-3 text-sm text-ok">
              {t('feedback.sent', { number: send.data.number })}
            </p>
            {send.data.failed > 0 && (
              <p role="alert" className="text-sm text-danger">
                {t('feedback.screenshotsFailed', { count: send.data.failed })}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <Link
                to="/feedback/$id"
                params={{ id: send.data.id }}
                onClick={onClose}
                className="inline-flex min-h-10 items-center rounded-md border border-control-border bg-sheet px-4 py-2 text-sm font-medium text-ink hover:border-ink"
              >
                {t('feedback.seeMine')}
              </Link>
              <Button onClick={onClose}>{t('feedback.close')}</Button>
            </div>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              send.mutate();
            }}
          >
            <fieldset className="grid grid-cols-2 gap-2">
              <legend className="sr-only">{t('feedback.kindLabel')}</legend>
              {(['bug', 'improvement'] as const).map((k) => {
                const Icon = k === 'bug' ? Bug : Lightbulb;
                const active = kind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setKind(k)}
                    className={`flex items-start gap-2 rounded-md border p-3 text-left text-sm ${
                      active
                        ? 'border-accent bg-brand-soft text-ink'
                        : 'border-control-border text-muted hover:border-ink'
                    }`}
                  >
                    <Icon size={18} strokeWidth={1.75} className="mt-0.5 shrink-0" aria-hidden />
                    <span className="flex flex-col">
                      <span className="font-medium text-ink">{t(`feedback.kinds.${k}`)}</span>
                      <span className="text-xs">{t(`feedback.kindHints.${k}`)}</span>
                    </span>
                  </button>
                );
              })}
            </fieldset>
            <Field label={t('feedback.title')}>
              <Input
                autoFocus
                value={title}
                maxLength={150}
                required
                minLength={3}
                placeholder={t(`feedback.titlePlaceholder.${kind}`)}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
            <Field label={t('feedback.description')}>
              <Textarea
                rows={5}
                value={description}
                maxLength={5000}
                required
                placeholder={t(`feedback.descriptionPlaceholder.${kind}`)}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <div className="flex flex-col gap-2 text-sm">
              <span className="font-medium">{t('feedback.screenshots')}</span>
              <span className="text-xs text-muted">{t('feedback.screenshotsHint')}</span>
              {shots.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {shots.map((s, i) => (
                    <li key={s.url} className="relative">
                      <img
                        src={s.url}
                        alt={s.file.name}
                        className="h-20 w-28 rounded-md border border-rule object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(s.url);
                          setShots((all) => all.filter((_, j) => j !== i));
                        }}
                        aria-label={t('feedback.removeScreenshot', { name: s.file.name })}
                        className="absolute -top-2 -right-2 rounded-full border border-rule bg-sheet p-0.5 text-muted hover:text-danger"
                      >
                        <X size={14} aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {shots.length < FEEDBACK_MAX_ATTACHMENTS && (
                <Button
                  variant="secondary"
                  className="self-start"
                  onClick={() => fileRef.current?.click()}
                >
                  <ImagePlus size={16} aria-hidden />
                  {t('feedback.addScreenshot')}
                </Button>
              )}
              {tooMany && (
                <span className="text-xs text-danger">
                  {t('feedback.tooMany', { max: FEEDBACK_MAX_ATTACHMENTS })}
                </span>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles([...(e.target.files ?? [])]);
                  e.target.value = '';
                }}
              />
            </div>
            <p className="rounded-md bg-paper p-3 text-xs text-muted">
              {t('feedback.contextNote', { page: pagePath })}
            </p>
            <ErrorBox error={send.error} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={send.isPending}>
                {send.isPending ? t('feedback.sending') : t('feedback.send')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
